import type { ShopVariant } from "@stackje/site-builder";
import { variantName } from "../../lib/shop";
import { currencySymbol, parseMoney } from "../../lib/shopMoney";
import { CheckRow, Field } from "../area";
import type { EditableVariant, ProductEditorState } from "./useProductEditor";

/**
 * The Price and Compare-at fields of one variant, with the currency's symbol in front. A price that isn't saved yet
 * is marked in the brand colour, and the save bar says so.
 */
export function PriceFields({ editor, variant, index }: { editor: ProductEditorState; variant: EditableVariant; index: number }) {
  const { product, busy, options, currency, language, updateVariant, priceChanged, compareChanged } = editor;
  const name = variantName(product, variant.values);
  const locked = busy || Boolean(options);
  const symbol = currencySymbol(currency, language);
  return <>
    <Field label="Price" hint={variant.priceText.trim() && parseMoney(variant.priceText, currency, language) === 0 ? "Free · download-only" : "Required before making available"} changed={priceChanged(variant)}>
      <span className="bw-area-prefixed"><span>{symbol}</span><input aria-label={`Price, ${name}`} inputMode="decimal" placeholder="Enter a price" disabled={locked} value={variant.priceText} onChange={(event) => updateVariant(index, { priceText: event.target.value })} /></span>
    </Field>
    <Field label="Compare-at" changed={compareChanged(variant)}>
      <span className="bw-area-prefixed"><span>{symbol}</span><input aria-label={`Compare-at price, ${name}`} inputMode="decimal" placeholder="Optional" disabled={locked} value={variant.compareText} onChange={(event) => updateVariant(index, { compareText: event.target.value })} /></span>
    </Field>
  </>;
}

/** Whether a variant's stock is counted and how many are in stock. Stock is live: it saves as soon as it changes. */
export function StockFields({ editor, variant }: { editor: ProductEditorState; variant: ShopVariant }) {
  const { api, siteId, shop, product, busy, work, setError } = editor;
  const stock = shop.stock.find((row) => row.variantId === variant.id);
  if (!stock) return null;
  return <div className="bw-shop-stock">
    <CheckRow label="Track stock" disabled={busy} checked={stock.tracked} onChange={(event) => {
      // Read the box now: the request runs later, after React has put the controlled box back.
      const tracked = event.target.checked;
      void work(() => api.saveVariantStock(siteId, variant.id, stock.rowVersion, { tracked, quantity: stock.quantity }), false);
    }} />
    {stock.tracked && <Field label="In stock">
      <input aria-label={`Stock, ${variantName(product, variant.values)}`} type="number" min={0} step={1} defaultValue={stock.quantity} key={stock.rowVersion} disabled={busy} onBlur={(event) => {
        const quantity = Number(event.target.value);
        if (!Number.isSafeInteger(quantity) || quantity < 0) { setError("Stock must be a whole non-negative number."); return; }
        if (quantity !== stock.quantity) void work(() => api.saveVariantStock(siteId, variant.id, stock.rowVersion, { tracked: true, quantity }), false);
      }} />
    </Field>}
  </div>;
}
